import { createClientSharedComposable, getStore } from '@global-store/store'
import type { LocationStore } from '@global-store/stores/location/types'
import { defineStore } from 'pinia'

import { isSSR } from '@fond-of/env'
import {
  type Locale,
  type UrlPrefix,
  defaultLocale,
  isValidUrlPrefix,
  localeMap,
} from '@fond-of/locale'

const localizedTopLevelDomains = ['de'] as const

type LocaleTopLevelDomain = (typeof localizedTopLevelDomains)[number]

export const useLocationStore = createClientSharedComposable(() =>
  defineStore('legacyLocation', {
    state: (): LocationStore => ({
      currentLocation: null,
    }),

    getters: {
      location(state) {
        if (!state.currentLocation && isSSR()) {
          // This error usually occurs when translations are loaded initially.
          throw new Error('Location need to be set before using it.')
        }

        // If the location is not set, we need to set it to the current location just for Client only apps
        // This can be removed if self-service and checkout are moved to storefront
        if (!state.currentLocation) {
          const location = new URL(globalThis.location.href)

          this.currentLocation = location

          return location
        }

        return state.currentLocation
      },
      localizedTopLevelDomain(): LocaleTopLevelDomain | undefined {
        return localizedTopLevelDomains.find((localizedDomain) =>
          this.location.hostname.endsWith(`.${localizedDomain}`)
        )
      },
      currentUrlPrefix(): UrlPrefix {
        if (this.localizedTopLevelDomain) {
          return this.localizedTopLevelDomain
        }

        const { pathname } = this.location
        const [_, legacyUrlPrefix] = pathname.split('/')
        const [urlPrefix] = legacyUrlPrefix.split('-')

        if (isValidUrlPrefix(urlPrefix)) {
          return urlPrefix as UrlPrefix
        }

        if (isValidUrlPrefix(legacyUrlPrefix)) {
          return legacyUrlPrefix as UrlPrefix
        }

        return defaultLocale as UrlPrefix
      },
      currentLocale(): Locale {
        const urlPrefix = this.currentUrlPrefix

        return localeMap[urlPrefix]
      },
      acceptLanguageHeader(): string {
        const languages = {
          de: 'de_DE,de;q=0.8',
          en: 'en_US,en;q=0.8',
          fr: 'fr_FR,fr;q=0.8',
          es: 'es_ES,es;q=0.8',
          da: 'da_DK,dk;q=0.8',
          it: 'it_IT,it;q=0.8',
        } as const

        return languages[this.currentLocale] || languages.en
      },
      languageCode(): string {
        const languageCodes = {
          de: 'de-DE',
          en: 'en-US',
          fr: 'fr-FR',
          es: 'es-ES',
          da: 'da-DK',
          it: 'it-IT',
        }

        return languageCodes[this.currentLocale] || languageCodes.en
      },
    },

    actions: {
      setLocation(newLocation: URL) {
        this.currentLocation = newLocation
      },
      getParams<
        TParams extends Record<string, string | string[] | undefined>,
      >(): TParams {
        return Object.fromEntries(
          this.location.searchParams.entries()
        ) as TParams
      },
    },

    hydrate: (storeState) => {
      storeState.currentLocation = new URL(globalThis.location.href)
    },
  })(getStore())
)
